import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useMutation } from '@apollo/client';
import { useQuery } from '@apollo/client';
import type { DialogRoot } from '@base-ui/react/dialog';
import clsx from 'clsx';
import { format } from 'date-fns';
import { CheckIcon, InfoIcon, PlusIcon, XIcon } from 'lucide-react';
import { Button } from 'primereact/button';
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { TabPanel, TabView, TabViewTabChangeEvent } from 'primereact/tabview';
import { Toast } from 'primereact/toast';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { useAppSelector } from '@/app/hooks';
import {
  useDeactivateSoScheduleMutation,
  useEditBulkDonorSchedulesMutation,
  useLazyGetDonorSchedulesByIdsQuery,
} from '@/app/services/donorSchedules';
import { DonorSchedulingActions } from '@/app/slices/donorScheduling';
import { PartnershipsActions } from '@/app/slices/partnerships';
import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { Button as UiButton } from '@/components/ui/button';
import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Dialog as UiDialog,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';
import {
  BILL_ACCOUNT_CANING_OPTION_VALUES,
  LOAD_DONOR_PARTNERS_OPTION_VALUES,
} from '@/enums';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  useDonorSchedulePastDueStatus,
  usePastDueStatus,
} from '@/hooks/usePastDueStatus';
import { DonorSchedule } from '@/rtk-query.gql';
import { queryClient } from '@/services';
import { useAccessTokenRef } from '@/services/auth';
import { useUpsertDonorSchedules } from '@/services/donor-scheduling/hooks';
import { partnershipsOptions } from '@/services/partnerships';
import { useSoScheduleWorkflow } from '@/services/workflows';
import { UPDATE_BILL_ACCOUNT_CANING_GROUPS } from '@/types.gql';
import { TOP_REVISIONS_BY_FERTILIZATION_SITE } from '@/types.gql';
import { runNewWorkflow, WORKFLOW } from '@/utils';
import { getDifferencesOnly } from '@/utils';
import { normalizeSubFamily } from '@/utils/subfamilyValues';

import { tabIndexMap, useAllowedNullFields } from '../../constants';
import { useMissingFields } from '../../hooks/useMissingFields';
import { removeNulls } from '../../util/util';
import {
  DonorScheduleFormEmbryos,
  DonorScheduleFormGeneral,
  DonorScheduleFormNotes,
  DonorScheduleFormSchedules,
  DonorScheduleFormSemen,
} from '../donor-schedule-form';
import { BULK_EDIT_FIELD_LABEL_KEYS } from '../donor-schedule-form/constants';
import { MissingFieldsDialog } from '../toolbar/MissingFieldsDialog';

const EXPORT_NO_VALUE = 100000000;

type EditDonorScheduleProps = {
  className?: string;
};

type PendingConfirm = {
  title: ReactNode;
  body: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  resolve: (confirmed: boolean) => void;
};

export const EditDonorSchedule = ({
  className = '',
}: EditDonorScheduleProps) => {
  const tokenRef = useAccessTokenRef();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [params, setParams] = useSearchParams();

  const id = params.get('id');
  const isRevisionMode = params.get('revision') === 'true';

  const [isPanelCollapsed, setIsPanelCollapsed] = useState(true);
  const dialogRef = useRef<Dialog>(null);
  const [dialogContainer, setDialogContainer] = useState<HTMLElement>();
  const [prevBillAccountCaningOption, setPrevBillAccountCaningOption] =
    useState<number | undefined>();

  const [upsertDonorSchedules] = useUpsertDonorSchedules();
  const [editBulkDonorSchedules] = useEditBulkDonorSchedulesMutation();

  const [updateBillAccountCaningGroups] = useMutation(
    UPDATE_BILL_ACCOUNT_CANING_GROUPS,
  );
  const [deactivateSoSchedule] = useDeactivateSoScheduleMutation();
  const isUpdateSoSchedulesEnabled = useFeatureFlag('Update_So_Schedules');
  const [runSoScheduleWorkflow] = useSoScheduleWorkflow();
  const [getDonorSchedulesByIds] = useLazyGetDonorSchedulesByIdsQuery();
  const savingToastRef = useRef<Toast>(null);
  const isBulkEditSavingToastEnabled = useFeatureFlag('Bulk_Edit_Saving_Toast');
  const isNoEmbryoForExportEnabled = useFeatureFlag('No_Embryo_For_Export');
  const isPreventDonorSireMismatchEnabled = useFeatureFlag(
    'Prevent_Donor_Schedule_Save_When_Donor_Sire_Do_Not_Match',
  );
  const isBulkEditCondensedViewEnabled = useFeatureFlag(
    'DS_Bulk_Edit_Condensed_View',
  );
  const isBaseUi = useFeatureFlag('DS_Edit_Dialog_Base_UI');
  const baseDialogPopupRef = useRef<HTMLDivElement>(null);
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(
    null,
  );
  const allowedNullFields = useAllowedNullFields();
  const tabViewClassName = clsx(
    'donor-schedule-tabs',
    'tab-view-shading',
    className,
  );

  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [soScheduleDeactivated, setSoScheduleDeactivated] = useState(false);

  const { partnership } = useAppSelector((state) => state.partnerships);
  const { selectedDonorSchedules, workingDonorSchedule, mergedDonorSchedules } =
    useAppSelector((state) => state.donorScheduling);

  const [unlockAll, setUnlockAll] = useState<boolean | null>(
    workingDonorSchedule.soSchedule === null,
  );

  const [lastSavedState, setLastSavedState] = useState<DonorSchedule | null>(
    workingDonorSchedule,
  );

  const { data: topRevisions } = useQuery(TOP_REVISIONS_BY_FERTILIZATION_SITE, {
    variables: {
      fertilizationSiteId: workingDonorSchedule?.fertilizationSiteId,
      date: workingDonorSchedule?.date,
    },
    skip:
      !workingDonorSchedule?.fertilizationSiteId ||
      isRevisionMode ||
      !workingDonorSchedule?.date,
  });

  const [revisionComment, setRevisionComment] = useState('');
  const [revisionNumber, setRevisionNumber] = useState<{
    previousRevision: string;
    currentRevision: string;
  }>({ previousRevision: '', currentRevision: '' });

  const getInitialTabIndex = () => {
    const revision = params.get('revision') === 'true';
    const tab = params.get('tab');

    if (revision && tab) {
      const targetIndex = tabIndexMap[tab];
      return targetIndex !== undefined ? targetIndex : 0;
    }
    return 0;
  };

  const [activeIndex, setActiveIndex] = useState(getInitialTabIndex);

  const selectedCount = useMemo(
    () => selectedDonorSchedules?.length ?? 0,
    [selectedDonorSchedules],
  );

  const { getMissingFields } = useMissingFields([workingDonorSchedule]);

  const showDialog = useMemo(() => params.get('edit') === 'true', [params]);
  const editingMultiple = useMemo(
    () => params.get('multiple') === 'true',
    [params],
  );

  // Clear the comment when switching away from an existing schedule (to another
  // one or to New) so it isn't carried over. Keeps it on a new schedule's first
  // save, when the id goes from empty to the created id.
  const workingScheduleId = workingDonorSchedule?.id;
  const [commentScheduleId, setCommentScheduleId] = useState(workingScheduleId);
  if (workingScheduleId !== commentScheduleId) {
    setCommentScheduleId(workingScheduleId);
    if (commentScheduleId) {
      setRevisionComment('');
    }
  }

  const initialRevisionComment = workingDonorSchedule?.revisionComment ?? '';
  useEffect(() => {
    if (revisionComment.length > 0) {
      return;
    }

    if (initialRevisionComment.length === 0) {
      return;
    }

    setRevisionComment(initialRevisionComment);
  }, [initialRevisionComment, revisionComment.length]);

  const topRevisionFromServer =
    (topRevisions?.topRevisionsByFertilizationSite as string | null) ?? null;
  useEffect(() => {
    if (!topRevisionFromServer) {
      return;
    }

    setRevisionNumber((prev) => {
      if (prev.previousRevision.length > 0) {
        return prev;
      }

      return {
        ...prev,
        previousRevision: topRevisionFromServer,
      };
    });
  }, [topRevisionFromServer]);

  const initialCurrentRevision = workingDonorSchedule?.revision ?? '';
  useEffect(() => {
    if (initialCurrentRevision.length === 0) {
      return;
    }

    setRevisionNumber((prev) => {
      if (prev.currentRevision.length > 0) {
        return prev;
      }

      return {
        ...prev,
        currentRevision: initialCurrentRevision,
      };
    });
  }, [initialCurrentRevision]);

  useEffect(() => {
    const refetchPartnerships = async () => {
      if (
        showDialog &&
        !editingMultiple &&
        tokenRef.current &&
        workingDonorSchedule?.donorId &&
        workingDonorSchedule?.id
      ) {
        try {
          const partnershipData = await queryClient.fetchQuery(
            partnershipsOptions(tokenRef.current, {
              donorId: workingDonorSchedule.donorId,
              donorScheduleId: workingDonorSchedule.id,
            }),
          );

          const partnership = partnershipData?.[0];

          if (partnership) {
            dispatch({
              type: PartnershipsActions.UPDATE_PARTNERSHIP,
              payload: [
                {
                  ...partnership,
                  donor: workingDonorSchedule?.donor,
                  donorId: workingDonorSchedule?.donorId,
                },
              ],
            });
          }
        } catch (error) {
          console.error('Failed to refetch partnerships:', error);
        }
      }
    };

    refetchPartnerships();
  }, [
    tokenRef,
    showDialog,
    editingMultiple,
    workingDonorSchedule?.donorId,
    workingDonorSchedule?.id,
    dispatch,
  ]);

  const isPartnership = useMemo(() => {
    const hasOwnership = workingDonorSchedule?.donor?.ownerships?.[0]?.id;
    const hasPartnerships =
      partnership.length > 0 &&
      (partnership[0].account1Id ||
        partnership[0].account2Id ||
        partnership[0].account3Id ||
        partnership[0].account4Id);
    return hasOwnership || hasPartnerships;
  }, [workingDonorSchedule?.donor?.ownerships, partnership]);

  const isLegacyOverAging60 = useMemo(() => {
    return !!workingDonorSchedule?.owner?.aging60;
  }, [workingDonorSchedule?.owner?.aging60]);

  const isBillingAccountPastDueEnabled = useFeatureFlag(
    'DS_Past_Due_Billing_Account',
  );

  const legacyPastDueStatus = usePastDueStatus(
    workingDonorSchedule?.owner,
    partnership,
    workingDonorSchedule?.loadDonorPartners ===
      LOAD_DONOR_PARTNERS_OPTION_VALUES.NO,
  );
  const billingAccountPastDueStatus = useDonorSchedulePastDueStatus(
    workingDonorSchedule,
    partnership,
  );

  const {
    isPastDue,
    ownerPastDue,
    partnersPastDue,
    hasAging60,
    hasOver90Days,
    isLocked,
    overrideActiveUntil,
  } = isBillingAccountPastDueEnabled
    ? billingAccountPastDueStatus
    : legacyPastDueStatus;

  const isOverAging60 = isBillingAccountPastDueEnabled
    ? hasAging60
    : isLegacyOverAging60;

  const isPastDueSaveRestricted = isLocked;

  const isExport = useMemo(() => {
    const exportValue = workingDonorSchedule?.export;
    if (!exportValue) return false;
    if (isNoEmbryoForExportEnabled) {
      return exportValue !== EXPORT_NO_VALUE;
    }
    return true;
  }, [workingDonorSchedule, isNoEmbryoForExportEnabled]);

  const isLowSemenQty = useMemo(() => {
    return workingDonorSchedule?.semen?.quantity < 3;
  }, [workingDonorSchedule]);

  const semenRebillRequired = useMemo(() => {
    return workingDonorSchedule?.creditRebill;
  }, [workingDonorSchedule]);

  const showSelectedDonorsSummary =
    isBulkEditCondensedViewEnabled && selectedCount > 1;

  const selectedDonorSummaries = useMemo<
    { id: string; name: string; registrationNumber: string | null }[]
  >(
    () =>
      showSelectedDonorsSummary
        ? selectedDonorSchedules
            .filter((ds: DonorSchedule) => Boolean(ds?.name))
            .map((ds: DonorSchedule) => ({
              id: ds.id,
              name: ds.name as string,
              registrationNumber: ds.donor?.registrationNumber || null,
            }))
        : [],
    [showSelectedDonorsSummary, selectedDonorSchedules],
  );

  const title = useMemo(() => {
    if (selectedCount > 1) {
      if (isBulkEditCondensedViewEnabled) {
        return t('donorScheduling.bulkEdit.editSelected', {
          count: selectedCount,
        });
      }
      const names = selectedDonorSchedules
        .map((ds: DonorSchedule) => ds?.name)
        .filter(Boolean)
        .join(', ');
      return `${t('common.editSelected', { count: selectedCount })}: ${names}`;
    }
    const donorName = workingDonorSchedule?.donor?.name;
    const sireName = workingDonorSchedule?.sire?.name;
    const formattedDate = workingDonorSchedule?.date
      ? format(workingDonorSchedule.date, 'MM/dd/yyyy')
      : undefined;
    const names =
      donorName && sireName
        ? `${donorName}${isPartnership ? '*' : ''} x ${sireName}`
        : donorName || sireName || '';
    if (names && formattedDate) {
      return `${names} - ${formattedDate}`;
    }
    return formattedDate || names || t('donorScheduling.newDonorSchedule');
  }, [
    isBulkEditCondensedViewEnabled,
    isPartnership,
    selectedCount,
    selectedDonorSchedules,
    t,
    workingDonorSchedule?.date,
    workingDonorSchedule?.donor?.name,
    workingDonorSchedule?.sire?.name,
  ]);

  useEffect(() => {
    if (selectedCount <= 1) {
      dispatch({ type: DonorSchedulingActions.UPDATE_NAME, payload: title });
    }
  }, [title, selectedCount, dispatch]);

  const isSaveDisabled = useMemo(() => {
    if (editingMultiple) return false;
    else {
      const requiredFieldsMissing =
        !workingDonorSchedule?.date ||
        !workingDonorSchedule?.aspirationSiteId ||
        !workingDonorSchedule?.csrId ||
        !workingDonorSchedule?.fertilizationSiteId;

      const billAccountRequired =
        workingDonorSchedule?.loadDonorPartners ===
          LOAD_DONOR_PARTNERS_OPTION_VALUES.NO &&
        !workingDonorSchedule?.billingAccountId;

      return requiredFieldsMissing || billAccountRequired;
    }
  }, [
    editingMultiple,
    workingDonorSchedule?.date,
    workingDonorSchedule?.aspirationSiteId,
    workingDonorSchedule?.csrId,
    workingDonorSchedule?.fertilizationSiteId,
    workingDonorSchedule?.loadDonorPartners,
    workingDonorSchedule?.billingAccountId,
  ]);

  const hasUnsavedChanges = useMemo(() => {
    return (
      JSON.stringify(workingDonorSchedule) !== JSON.stringify(lastSavedState)
    );
  }, [workingDonorSchedule, lastSavedState]);

  const trackedBulkEditChanges = useMemo(() => {
    if (!editingMultiple || !mergedDonorSchedules) return {};
    return getDifferencesOnly(mergedDonorSchedules, workingDonorSchedule);
  }, [editingMultiple, mergedDonorSchedules, workingDonorSchedule]);

  const awaitConfirmDialog = useCallback(
    (options: Parameters<typeof confirmDialog>[0]): Promise<boolean> =>
      new Promise((resolve) => {
        if (isBaseUi) {
          setPendingConfirm((previous) => {
            previous?.resolve(false);
            return {
              title:
                typeof options?.header === 'function' ? null : options?.header,
              body:
                typeof options?.message === 'function'
                  ? null
                  : options?.message,
              confirmLabel: options?.acceptLabel,
              cancelLabel: options?.rejectLabel,
              resolve,
            };
          });
          return;
        }
        confirmDialog({
          ...options,
          accept: () => resolve(true),
          reject: () => resolve(false),
        });
      }),
    [isBaseUi],
  );

  const handlePendingConfirmAccept = () => {
    pendingConfirm?.resolve(true);
    setPendingConfirm(null);
  };

  const handlePendingConfirmOpenChange = (open: boolean) => {
    if (open) return;
    pendingConfirm?.resolve(false);
    setPendingConfirm(null);
  };

  const pickPayload = useCallback(
    (data: any) =>
      data?.upsertDonorSchedules ?? data?.editBulkDonorSchedules ?? null,
    [],
  );

  const handleSaveDonorSchedule = useCallback(async () => {
    try {
      setLastSavedState(workingDonorSchedule);
      const donorScheduleInput = {
        ...workingDonorSchedule,
        revision: revisionNumber.currentRevision,
        revisionComment: revisionComment,
        annotation: {
          ...workingDonorSchedule?.annotation,
          noteText: revisionComment,
        },
      };
      let newSchedule;
      if (editingMultiple) {
        if (Object.keys(trackedBulkEditChanges).length === 0) {
          dispatch(
            addToast({
              severity: 'warn',
              summary: t('donorScheduling.noChanges'),
              detail: t('donorScheduling.noChangesDetectedBulkEdit'),
              life: ToastLife.SHORT,
            }),
          );
          return;
        }

        const confirmed = await awaitConfirmDialog({
          message: (
            <div>
              <div>{t('donorScheduling.bulkEditChangesApplied')}</div>
              <ul style={{ marginLeft: 20 }}>
                {(() => {
                  const labelKeySet = new Set<string>();
                  const labels: string[] = [];
                  for (const key of Object.keys(trackedBulkEditChanges)) {
                    const labelKey =
                      BULK_EDIT_FIELD_LABEL_KEYS[key] ??
                      'donorScheduling.bulkEdit.fallback';
                    const label =
                      labelKey === 'donorScheduling.bulkEdit.fallback'
                        ? t(labelKey, { field: key })
                        : t(labelKey);
                    if (!labelKeySet.has(labelKey)) {
                      labelKeySet.add(labelKey);
                      labels.push(label);
                    }
                  }
                  return labels.map((label, index) => (
                    <li key={index}>{label}</li>
                  ));
                })()}
              </ul>
              <div>{t('donorScheduling.doYouWantToProceed')}</div>
            </div>
          ),
          header: t('donorScheduling.editingMultipleDonorSchedules'),
          icon: 'pi pi-exclamation-triangle',
          acceptLabel: t('common.yes'),
          rejectLabel: t('common.no'),
          acceptClassName: 'p-button-warning',
          rejectClassName: 'p-button-secondary',
        });

        if (!confirmed) return; // user clicked "No"

        let baseSavingToastId: string | undefined;
        if (isBulkEditSavingToastEnabled && isBaseUi) {
          baseSavingToastId = toast.add({
            data: { variant: 'loading' },
            title: t('donorScheduling.bulkEdit.savingSummary'),
            description: t('donorScheduling.bulkEdit.savingDetail'),
            timeout: 0,
          });
        } else if (isBulkEditSavingToastEnabled) {
          savingToastRef.current?.show({
            severity: 'info',
            icon: 'pi pi-spin pi-spinner',
            summary: t('donorScheduling.bulkEdit.savingSummary'),
            detail: t('donorScheduling.bulkEdit.savingDetail'),
            sticky: true,
            closable: false,
          });
        }
        try {
          newSchedule = await editBulkDonorSchedules({
            input: trackedBulkEditChanges as DonorSchedule,
            bulkUpdateIds:
              selectedDonorSchedules.length > 1
                ? selectedDonorSchedules.map((ds: DonorSchedule) => ds.id)
                : undefined,
          });
        } finally {
          if (baseSavingToastId) {
            toast.close(baseSavingToastId);
          }
          savingToastRef.current?.clear();
        }
      } else {
        const cleanedInput = removeNulls(donorScheduleInput, allowedNullFields);
        newSchedule = await upsertDonorSchedules({
          input: cleanedInput as DonorSchedule,
          bulkUpdateIds:
            selectedDonorSchedules.length > 1
              ? selectedDonorSchedules.map((ds: DonorSchedule) => ds.id)
              : undefined,
        });
      }

      const payload = pickPayload(newSchedule?.data);
      const resultCode: number | undefined = payload?.code;
      const resultMessage: string | undefined = payload?.message;
      const newDonorScheduleId: string | undefined =
        payload?.donorSchedules?.[0]?.id;

      const caningOption = workingDonorSchedule?.billAccountCaningOption;
      const loadDonorPartners = workingDonorSchedule?.loadDonorPartners;
      const shouldUpdate =
        (caningOption === BILL_ACCOUNT_CANING_OPTION_VALUES.GROUPS_OF_THREE ||
          caningOption === BILL_ACCOUNT_CANING_OPTION_VALUES.ODDS_AND_EVENS) &&
        loadDonorPartners === LOAD_DONOR_PARTNERS_OPTION_VALUES.NO &&
        caningOption !== prevBillAccountCaningOption;

      if (shouldUpdate) {
        const idsToUpdate =
          selectedDonorSchedules.length > 1
            ? selectedDonorSchedules
                .map((ds: DonorSchedule) => ds.id)
                .filter(Boolean)
            : [newDonorScheduleId ?? workingDonorSchedule?.id].filter(Boolean);

        for (const donorScheduleId of idsToUpdate) {
          await updateBillAccountCaningGroups({
            variables: {
              donorScheduleId,
            },
          });
        }

        setPrevBillAccountCaningOption(
          workingDonorSchedule?.billAccountCaningOption,
        );
      }

      dispatch({
        type: DonorSchedulingActions.INCREMENT_REFETCH_TRIGGER,
      });

      if (!editingMultiple && newDonorScheduleId) {
        dispatch({
          type: DonorSchedulingActions.UPDATE_ID,
          payload: newDonorScheduleId,
        });
      }

      if (resultCode === 200) {
        dispatch(
          addToast({
            severity: 'success',
            summary:
              selectedDonorSchedules.length > 1
                ? t('donorScheduling.bulkSchedulesSaved')
                : t('donorScheduling.donorScheduleSaved'),
            detail:
              selectedDonorSchedules.length > 1
                ? t('donorScheduling.bulkSchedulesSavedSuccessfully')
                : t('donorScheduling.scheduleSavedSuccessfully', {
                    name: workingDonorSchedule.name,
                  }),
            life: ToastLife.DEFAULT,
          }),
        );
        return {
          success: true,
          newDonorScheduleId: newDonorScheduleId ?? null,
        };
      } else {
        dispatch(
          addToast({
            severity: 'error',
            summary: t('donorScheduling.saveFailed'),
            detail:
              selectedDonorSchedules.length > 1
                ? t('donorScheduling.failedToSaveBulkSchedules', {
                    message: resultMessage ?? '',
                  })
                : t('donorScheduling.failedToSaveSchedule', {
                    name: workingDonorSchedule.name,
                    message: resultMessage ?? '',
                  }),
            life: ToastLife.DEFAULT,
          }),
        );
        return {
          success: false,
          newDonorScheduleId: newDonorScheduleId ?? null,
        };
      }
    } catch (error) {
      console.error('Failed to save donorSchedule:', error);
      dispatch(
        addToast({
          severity: 'error',
          summary: t('donorScheduling.saveFailed'),
          detail:
            selectedDonorSchedules.length > 1
              ? t('donorScheduling.failedToSaveBulkSchedules', {
                  message: error?.toString(),
                })
              : t('donorScheduling.failedToSaveSchedule', {
                  name: workingDonorSchedule.name,
                  message: error?.toString(),
                }),
          life: ToastLife.DEFAULT,
        }),
      );
      return { success: false, newDonorScheduleId: null };
    }
  }, [
    workingDonorSchedule,
    revisionNumber.currentRevision,
    revisionComment,
    editingMultiple,
    pickPayload,
    prevBillAccountCaningOption,
    dispatch,
    trackedBulkEditChanges,
    awaitConfirmDialog,
    t,
    editBulkDonorSchedules,
    isBulkEditSavingToastEnabled,
    isBaseUi,
    selectedDonorSchedules,
    allowedNullFields,
    upsertDonorSchedules,
    updateBillAccountCaningGroups,
  ]);

  const validatePartnershipAccounts = useCallback(() => {
    const isBillPartnership =
      workingDonorSchedule?.loadDonorPartners ===
      LOAD_DONOR_PARTNERS_OPTION_VALUES.YES;

    if (!isBillPartnership || !partnership?.[0]) {
      return true;
    }

    const accountsWithPercentages = [
      partnership[0].percent1,
      partnership[0].percent2,
      partnership[0].percent3,
      partnership[0].percent4,
    ].filter((percent) => percent != null && percent > 0).length;

    const accountsWithIds = [
      partnership[0].account1Id,
      partnership[0].account2Id,
      partnership[0].account3Id,
      partnership[0].account4Id,
    ].filter((id) => id != null).length;

    if (
      accountsWithPercentages > 0 &&
      accountsWithIds !== accountsWithPercentages
    ) {
      dispatch(
        addToast({
          severity: 'warn',
          summary: t('donorScheduling.savePartnership'),
          detail:
            t('donorScheduling.incompletePartnershipAccounts', {
              required: accountsWithPercentages,
              provided: accountsWithIds,
            }) ||
            `Partnership requires ${accountsWithPercentages} accounts but only ${accountsWithIds} are set`,
          life: ToastLife.SHORT,
        }),
      );
      return false;
    }

    return true;
  }, [dispatch, partnership, workingDonorSchedule?.loadDonorPartners, t]);

  const validateDonorSireSpecies = useCallback(() => {
    if (!isPreventDonorSireMismatchEnabled) return true;

    const donorSpecies = normalizeSubFamily(
      workingDonorSchedule?.donor?.breed?.subFamily,
    );
    const sireSpecies = normalizeSubFamily(
      workingDonorSchedule?.sire?.breed?.subFamily,
    );

    if (!donorSpecies || !sireSpecies) return true;
    if (donorSpecies === sireSpecies) return true;

    dispatch(
      addToast({
        severity: 'error',
        summary: t('donorScheduling.speciesMismatch', {
          defaultValue: 'Species Mismatch',
        }),
        detail: t('donorScheduling.speciesMismatchDetail', {
          defaultValue:
            'The selected sire must match the donor species. Please select a sire with the same species as the donor.',
        }),
        life: ToastLife.DEFAULT,
      }),
    );
    return false;
  }, [
    dispatch,
    isPreventDonorSireMismatchEnabled,
    workingDonorSchedule?.donor?.breed?.subFamily,
    workingDonorSchedule?.sire?.breed?.subFamily,
    t,
  ]);

  const handleSave = useCallback(async () => {
    if (id !== '-1') {
      if (!validatePartnershipAccounts()) {
        return;
      }
    }

    if (!validateDonorSireSpecies()) {
      return;
    }

    if (id === '-1' && isPastDue) {
      const confirmed = await awaitConfirmDialog({
        message: t('donorScheduling.pastDueWarningMessage', {
          defaultValue:
            'This account is past due. Are you sure you want to create a donor schedule for this account?',
        }),
        header: t('donorScheduling.pastDueWarning', {
          defaultValue: 'Past Due Account Warning',
        }),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: t('common.yes'),
        rejectLabel: t('common.no'),
        acceptClassName: 'p-button-warning',
        rejectClassName: 'p-button-secondary',
      });

      if (!confirmed) return;
    }

    const result = await handleSaveDonorSchedule();

    if (result?.success && result.newDonorScheduleId) {
      const newParams = new URLSearchParams(params);
      newParams.set('id', result.newDonorScheduleId);
      newParams.delete('multiple');
      setParams(newParams);
    }
  }, [
    handleSaveDonorSchedule,
    params,
    setParams,
    validatePartnershipAccounts,
    validateDonorSireSpecies,
    id,
    isPastDue,
    awaitConfirmDialog,
    t,
  ]);

  useEffect(() => {
    const saveInterval = setInterval(async () => {
      if (
        hasUnsavedChanges &&
        id !== '-1' &&
        isPanelCollapsed &&
        workingDonorSchedule?.date &&
        workingDonorSchedule?.owner &&
        !isPastDueSaveRestricted &&
        !soScheduleDeactivated &&
        !isRevisionMode &&
        !(
          isPreventDonorSireMismatchEnabled &&
          workingDonorSchedule?.donor?.breed?.subFamily &&
          workingDonorSchedule?.sire?.breed?.subFamily &&
          normalizeSubFamily(workingDonorSchedule?.donor?.breed?.subFamily) !==
            normalizeSubFamily(workingDonorSchedule?.sire?.breed?.subFamily)
        )
      ) {
        try {
          await handleSave();
          setLastSavedState(workingDonorSchedule);
        } catch (error) {
          console.error('Auto-save failed:', error);
        }
      }
    }, 3000);

    return () => clearInterval(saveInterval);
  }, [
    handleSave,
    hasUnsavedChanges,
    id,
    isPastDueSaveRestricted,
    selectedCount,
    workingDonorSchedule,
    isPanelCollapsed,
    soScheduleDeactivated,
    params,
    isRevisionMode,
  ]);

  const handleCollapseChange = (collapsed: boolean) => {
    if (workingDonorSchedule?.donorId) {
      setIsPanelCollapsed(collapsed);
    } else {
      dispatch(
        addToast({
          severity: 'warn',
          summary: t('donorScheduling.selectDonor'),
          detail: t('donorScheduling.selectDonorDetail'),
          life: ToastLife.SHORT,
        }),
      );
    }
  };

  const handleCancel = () => {
    const newParams = new URLSearchParams(params);
    newParams.delete('edit');
    newParams.delete('multiple');
    if (isRevisionMode) {
      newParams.delete('id');
    }
    let reset = false;
    if (!selectedDonorSchedules[0]?.id) {
      dispatch({
        type: DonorSchedulingActions.RESET_WORKING_DONOR_SCHEDULE,
      });
      dispatch({
        type: PartnershipsActions.RESET_PARTNERSHIP,
      });
    }
    reset = true;
    if (reset) {
      newParams.set('reset', 'true');
    }
    setParams(newParams);
  };

  const handleSaveAndClose = () => {
    const missingFields = getMissingFields();

    if (missingFields.length > 0) {
      setShowConfirmationDialog(true);
    } else {
      proceedWithSaveAndClose();
    }
  };

  const proceedWithSaveAndClose = async () => {
    if (id !== '-1') {
      if (!validatePartnershipAccounts()) {
        return;
      }
    }

    if (!validateDonorSireSpecies()) {
      return;
    }

    const result = await handleSaveDonorSchedule();
    if (result?.success) {
      handleCancel();
    }
  };

  const handleConfirmationDialogClose = () => {
    setShowConfirmationDialog(false);
  };

  const getAutoSaveIcon = useCallback(() => {
    if (selectedCount > 1 && id === '-1') return 'pi-ban';
    if (id === '-1') return 'pi-ban';
    if (isPastDueSaveRestricted) return 'pi-ban';
    if (soScheduleDeactivated) return 'pi-ban';
    if (params.get('revision') === 'true') return 'pi-ban';
    return isPanelCollapsed ? 'pi-check' : 'pi-ban';
  }, [
    selectedCount,
    id,
    isPastDueSaveRestricted,
    soScheduleDeactivated,
    params,
    isPanelCollapsed,
  ]);

  const getAutoSaveMessage = useCallback(() => {
    if (selectedCount > 1 && id === '-1') {
      return t('donorScheduling.autosaveDisabledBulkEdit');
    }
    if (id === '-1') {
      return t('donorScheduling.autosaveDisabledUntilCreated');
    }
    if (isPastDueSaveRestricted) {
      return t('donorScheduling.autosaveDisabledPastDue');
    }
    if (soScheduleDeactivated) {
      return t('donorScheduling.autosaveDisabledUntilSoSchedule');
    }
    if (params.get('revision') === 'true') {
      return t('donorScheduling.autosaveDisabledRevisionMode');
    }
    return isPanelCollapsed
      ? t('donorScheduling.autosaveEnabled')
      : t('donorScheduling.autosaveDisabled');
  }, [
    selectedCount,
    id,
    isPastDueSaveRestricted,
    soScheduleDeactivated,
    params,
    isPanelCollapsed,
    t,
  ]);

  // Read inside the workflow callback, which can fire after another donor schedule is opened.
  const workingDonorScheduleIdRef = useRef(workingDonorSchedule?.id);
  useEffect(() => {
    workingDonorScheduleIdRef.current = workingDonorSchedule?.id;
  }, [workingDonorSchedule?.id]);

  const loadSoScheduleAfterWorkflow = useCallback(
    async (donorScheduleId: string) => {
      try {
        const { data } = await getDonorSchedulesByIds({
          ids: [donorScheduleId],
        });
        const soSchedule = data?.donorSchedules.items.find(
          (schedule) => schedule.id === donorScheduleId,
        )?.soSchedule;
        if (
          soSchedule &&
          workingDonorScheduleIdRef.current === donorScheduleId
        ) {
          dispatch({
            type: DonorSchedulingActions.UPDATE_SO_SCHEDULE,
            payload: soSchedule,
          });
        }
      } finally {
        dispatch({ type: DonorSchedulingActions.INCREMENT_REFETCH_TRIGGER });
      }
    },
    [getDonorSchedulesByIds, dispatch],
  );

  const handleCreateSoSchedule = useCallback(async () => {
    await handleSave();
    if (isUpdateSoSchedulesEnabled) {
      const donorScheduleId = workingDonorSchedule?.id;
      if (!donorScheduleId) return;
      // The workflow runs in the background; the progress toast tracks it, and the new SO
      // schedule is loaded into the form once it finishes.
      try {
        await runSoScheduleWorkflow(
          { ids: [donorScheduleId] },
          { onFinished: () => loadSoScheduleAfterWorkflow(donorScheduleId) },
        );
      } catch {
        // The workflow toast already shows why it couldn't start.
        return;
      }
      setUnlockAll(false);
      setSoScheduleDeactivated(false);
      return;
    }
    await runNewWorkflow(
      [{ RecordId: workingDonorSchedule?.id }],
      WORKFLOW.SOSchedule.id,
    );
    dispatch(
      addToast({
        severity: 'success',
        summary: t('donorScheduling.workflowExecuted'),
        detail: t('donorScheduling.soScheduleWorkflowCompleted'),
        life: ToastLife.DEFAULT,
      }),
    );
    setUnlockAll(false);
    setSoScheduleDeactivated(false);
  }, [
    handleSave,
    isUpdateSoSchedulesEnabled,
    runSoScheduleWorkflow,
    loadSoScheduleAfterWorkflow,
    workingDonorSchedule,
    dispatch,
    setUnlockAll,
    setSoScheduleDeactivated,
  ]);

  const handleSoScheduleDialogYes = useCallback(async () => {
    const deactivateSoScheduleResponse = await deactivateSoSchedule({
      donorScheduleId: workingDonorSchedule?.id,
    }).unwrap();
    if (deactivateSoScheduleResponse?.deactivateSoSchedule === true) {
      if (setSoScheduleDeactivated) {
        setSoScheduleDeactivated(true);
      }
      if (setUnlockAll) {
        setUnlockAll(true);
      }
      dispatch({
        type: DonorSchedulingActions.UPDATE_SO_SCHEDULE,
        payload: null,
      });
    }
  }, [
    deactivateSoSchedule,
    workingDonorSchedule,
    setSoScheduleDeactivated,
    setUnlockAll,
    dispatch,
  ]);

  const renderFooter = () => {
    return (
      <div className="flex w-full items-center justify-between">
        <div className="flex items-center gap-2">
          <i className={`pi ${getAutoSaveIcon()} flex items-center text-sm`} />
          <span className="text-500 flex items-center text-sm">
            {getAutoSaveMessage()}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {soScheduleDeactivated ? (
            <Button
              label={t('donorScheduling.createSoSchedule')}
              icon="pi pi-plus"
              onClick={handleCreateSoSchedule}
              className="p-button-text"
            />
          ) : (
            <>
              <Button
                label={
                  id === '-1' && !editingMultiple
                    ? t('common.cancel')
                    : t('common.saveAndClose')
                }
                icon="pi pi-times"
                onClick={
                  id === '-1' && !editingMultiple
                    ? handleCancel
                    : handleSaveAndClose
                }
                className="p-button-text"
              />
              <Button
                label={
                  id === '-1' && selectedCount <= 1
                    ? t('common.create')
                    : t('common.save')
                }
                icon={
                  id === '-1' && selectedCount <= 1
                    ? 'pi pi-plus'
                    : 'pi pi-check'
                }
                onClick={handleSave}
                disabled={
                  isSaveDisabled || (isPastDueSaveRestricted && id !== '-1')
                }
                autoFocus
              />
            </>
          )}
        </div>
      </div>
    );
  };

  const renderConfirmationDialogFooter = () => {
    return (
      <>
        <div className="rounded-lg p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <i className="pi pi-info-circle mt-0.5"></i>
              <p className="m-0 text-left text-sm">
                {t('donorScheduling.completeMissingFieldsEachDonor')}
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                label={t('common.no')}
                icon="pi pi-times"
                onClick={handleConfirmationDialogClose}
                className="p-button-text"
              />
              <Button
                label={t('common.yes')}
                icon="pi pi-check"
                onClick={() => {
                  handleConfirmationDialogClose();
                  proceedWithSaveAndClose();
                }}
                autoFocus
              />
            </div>
          </div>
        </div>
      </>
    );
  };

  const handleTabChange = (e: TabViewTabChangeEvent) => {
    setActiveIndex(e.index);
  };

  const handleBaseDialogOpenChange = (
    open: boolean,
    eventDetails: DialogRoot.ChangeEventDetails,
  ) => {
    if (open) return;
    const isEscapeFromOutsidePopup =
      eventDetails.reason === 'escape-key' &&
      !baseDialogPopupRef.current?.contains(
        eventDetails.event.target as Node | null,
      );
    if (isEscapeFromOutsidePopup) return;
    handleCancel();
  };

  const isCreatingSingle = id === '-1' && selectedCount <= 1;
  const isCancelInsteadOfSaveAndClose = id === '-1' && !editingMultiple;

  const renderBaseFooter = () => (
    <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="text-muted-foreground flex items-center gap-2 text-sm">
        <i className={`pi ${getAutoSaveIcon()} flex items-center text-sm`} />
        <span>{getAutoSaveMessage()}</span>
      </div>
      <div className="flex items-center justify-end gap-2">
        {soScheduleDeactivated ? (
          <UiButton variant="ghost" onClick={handleCreateSoSchedule}>
            <PlusIcon />
            {t('donorScheduling.createSoSchedule')}
          </UiButton>
        ) : (
          <>
            <UiButton
              variant="outline"
              onClick={
                isCancelInsteadOfSaveAndClose
                  ? handleCancel
                  : handleSaveAndClose
              }
            >
              <XIcon />
              {isCancelInsteadOfSaveAndClose
                ? t('common.cancel')
                : t('common.saveAndClose')}
            </UiButton>
            <UiButton
              onClick={handleSave}
              disabled={
                isSaveDisabled || (isPastDueSaveRestricted && id !== '-1')
              }
            >
              {isCreatingSingle ? <PlusIcon /> : <CheckIcon />}
              {isCreatingSingle ? t('common.create') : t('common.save')}
            </UiButton>
          </>
        )}
      </div>
    </div>
  );

  const renderBaseConfirmationDialogFooter = () => (
    <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-2">
        <InfoIcon className="size-4 shrink-0" />
        <p className="m-0 text-left text-sm">
          {t('donorScheduling.completeMissingFieldsEachDonor')}
        </p>
      </div>
      <div className="flex justify-end gap-2">
        <UiButton variant="outline" onClick={handleConfirmationDialogClose}>
          <XIcon />
          {t('common.no')}
        </UiButton>
        <UiButton
          onClick={() => {
            handleConfirmationDialogClose();
            proceedWithSaveAndClose();
          }}
        >
          <CheckIcon />
          {t('common.yes')}
        </UiButton>
      </div>
    </div>
  );

  const dialogBanners = (
    <>
      {showSelectedDonorsSummary && (
        <div
          className="mb-4 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700"
          data-testid="bulk-edit-selected-donors"
        >
          <div className="font-medium">
            {t('donorScheduling.bulkEdit.selectedDonors', {
              count: selectedCount,
            })}
          </div>
          {selectedDonorSummaries.length > 0 && (
            <ul className="m-0 mt-1 max-h-24 list-none overflow-y-auto p-0 text-slate-600">
              {selectedDonorSummaries.map(
                ({ id, name, registrationNumber }, index) => (
                  <li key={`${id}-${index}`}>
                    {name}
                    {registrationNumber && (
                      <span className="ml-1 text-slate-500">
                        {t('donorScheduling.bulkEdit.registrationNumber', {
                          registrationNumber,
                        })}
                      </span>
                    )}
                  </li>
                ),
              )}
            </ul>
          )}
        </div>
      )}
      {isExport && (
        <div className="mb-4 rounded-lg bg-yellow-400 p-4 text-center font-semibold text-black">
          <span>{t('donorScheduling.embryoForExport')}</span>
        </div>
      )}
      {isLowSemenQty && (
        <div className="mb-4 rounded-lg bg-yellow-400 p-4 text-center font-semibold text-black">
          <span>{t('donorScheduling.lowSemenQty')}</span>
        </div>
      )}
      {semenRebillRequired && (
        <div className="mb-4 rounded-lg bg-yellow-400 p-4 text-center font-semibold text-black">
          <span>{t('donorScheduling.semenRebillRequired')}</span>
        </div>
      )}
      {params.get('revision') === 'true' && (
        <div className="mb-4 rounded-lg bg-yellow-400 p-4 text-center font-semibold text-black">
          <span>{t('donorScheduling.revisionMode')}</span>
        </div>
      )}
      {isPastDue && (hasOver90Days || isLocked) ? (
        <div className="mb-4 rounded-lg bg-red-500 p-4 text-center font-semibold text-white">
          <span>
            {isLocked
              ? hasOver90Days
                ? 'Account or partner is over 90 days and is restricted/closed'
                : 'Account or partner is restricted/closed (TOG 90-day status)'
              : overrideActiveUntil
                ? `Account or partner is over 90 days but override is active until ${overrideActiveUntil}`
                : partnersPastDue && !ownerPastDue
                  ? 'Past Due Account (On Partnership)'
                  : t('donorScheduling.pastDue')}
          </span>
        </div>
      ) : isPastDue || isOverAging60 ? (
        <div className="mb-4 rounded-lg bg-yellow-400 p-4 text-center font-semibold text-black">
          <span>
            {hasAging60
              ? t('donorScheduling.overAging60')
              : t('donorScheduling.pastDue')}
          </span>
        </div>
      ) : null}
    </>
  );

  const generalForm = (
    <DonorScheduleFormGeneral
      className="flex flex-col gap-7"
      dialogContainer={isBaseUi ? undefined : dialogContainer}
      collapsed={isPanelCollapsed}
      viewOnly={false}
      onCollapseChange={handleCollapseChange}
      unlockAll={unlockAll}
      setUnlockAll={setUnlockAll}
      handleSoScheduleDialogYes={handleSoScheduleDialogYes}
      revisionComment={revisionComment}
      setRevisionComment={setRevisionComment}
      revisionNumber={revisionNumber}
      setRevisionNumber={setRevisionNumber}
    />
  );

  const embryosForm = (
    <DonorScheduleFormEmbryos
      className="flex flex-col gap-7"
      collapsed={isPanelCollapsed}
      viewOnly={false}
      onCollapseChange={handleCollapseChange}
    />
  );

  const schedulesForm = (
    <DonorScheduleFormSchedules
      className="flex flex-col gap-7"
      collapsed={isPanelCollapsed}
      viewOnly={false}
      onCollapseChange={handleCollapseChange}
      unlockAll={unlockAll}
      setUnlockAll={setUnlockAll}
      handleSoScheduleDialogYes={handleSoScheduleDialogYes}
    />
  );

  const semenForm = (
    <DonorScheduleFormSemen
      className="flex flex-col gap-7"
      collapsed={isPanelCollapsed}
      viewOnly={false}
      onCollapseChange={handleCollapseChange}
      revisionComment={revisionComment}
      setRevisionComment={setRevisionComment}
      revisionNumber={revisionNumber}
      setRevisionNumber={setRevisionNumber}
    />
  );

  const notesForm = (
    <DonorScheduleFormNotes
      className="flex flex-col gap-7"
      viewOnly={false}
      revisionComment={revisionComment}
      setRevisionComment={setRevisionComment}
      revisionNumber={revisionNumber}
      setRevisionNumber={setRevisionNumber}
    />
  );

  if (isBaseUi) {
    return (
      <>
        <UiDialog
          open={showDialog}
          onOpenChange={handleBaseDialogOpenChange}
          disablePointerDismissal
        >
          <DialogContent
            ref={baseDialogPopupRef}
            preflight={false}
            className={clsx(
              'edit-donor-schedule-dialog md:w-[60vw] md:max-w-none',
              className,
            )}
          >
            <DialogHeader className="preflight pr-8">
              <DialogTitle>
                {title || t('donorScheduling.newDonorSchedule')}
              </DialogTitle>
            </DialogHeader>
            <DialogBody>
              {dialogBanners}
              <Tabs
                className={clsx('donor-schedule-tabs', className)}
                value={activeIndex}
                onValueChange={(value) => setActiveIndex(value as number)}
              >
                <TabsList className="preflight">
                  <TabsTrigger value={0}>{t('common.general')}</TabsTrigger>
                  <TabsTrigger value={1}>{t('embryo.embryos')}</TabsTrigger>
                  <TabsTrigger value={2}>
                    {t('donorScheduling.schedules')}
                  </TabsTrigger>
                  <TabsTrigger value={3}>{t('semen.semen')}</TabsTrigger>
                  <TabsTrigger value={4}>{t('common.notes')}</TabsTrigger>
                </TabsList>
                <TabsContent value={0}>{generalForm}</TabsContent>
                <TabsContent value={1}>{embryosForm}</TabsContent>
                <TabsContent value={2}>{schedulesForm}</TabsContent>
                <TabsContent value={3}>{semenForm}</TabsContent>
                <TabsContent value={4}>{notesForm}</TabsContent>
              </Tabs>
            </DialogBody>
            <DialogFooter className="preflight">
              {renderBaseFooter()}
            </DialogFooter>
          </DialogContent>
        </UiDialog>
        <UiDialog
          open={pendingConfirm !== null}
          onOpenChange={handlePendingConfirmOpenChange}
        >
          <DialogContent className="md:max-w-md">
            <DialogHeader className="pr-8">
              <DialogTitle>{pendingConfirm?.title}</DialogTitle>
            </DialogHeader>
            <DialogBody>{pendingConfirm?.body}</DialogBody>
            <DialogFooter>
              <UiButton
                variant="outline"
                onClick={() => handlePendingConfirmOpenChange(false)}
              >
                {pendingConfirm?.cancelLabel ?? t('common.no')}
              </UiButton>
              <UiButton variant="warning" onClick={handlePendingConfirmAccept}>
                {pendingConfirm?.confirmLabel ?? t('common.yes')}
              </UiButton>
            </DialogFooter>
          </DialogContent>
        </UiDialog>
        <MissingFieldsDialog
          visible={showConfirmationDialog}
          onHide={handleConfirmationDialogClose}
          selectedDonorSchedules={[workingDonorSchedule]}
          renderFooter={renderBaseConfirmationDialogFooter}
          variant="base-ui"
        />
      </>
    );
  }

  return (
    <>
      <Toast ref={savingToastRef} />
      <Dialog
        ref={dialogRef}
        header={title || t('donorScheduling.newDonorSchedule')}
        visible={showDialog}
        style={{ width: '60vw' }}
        onHide={handleCancel}
        footer={renderFooter()}
        className={`edit-donor-schedule-dialog ${className}`}
        onShow={() =>
          setDialogContainer(dialogRef.current?.getMask() ?? undefined)
        }
      >
        {dialogBanners}
        <TabView
          className={tabViewClassName}
          activeIndex={activeIndex}
          onTabChange={handleTabChange}
        >
          <TabPanel header={t('common.general')}>{generalForm}</TabPanel>
          <TabPanel header={t('embryo.embryos')}>{embryosForm}</TabPanel>
          <TabPanel header={t('donorScheduling.schedules')}>
            {schedulesForm}
          </TabPanel>
          <TabPanel header={t('semen.semen')}>{semenForm}</TabPanel>
          <TabPanel header={t('common.notes')}>{notesForm}</TabPanel>
        </TabView>
      </Dialog>
      <MissingFieldsDialog
        visible={showConfirmationDialog}
        onHide={handleConfirmationDialogClose}
        selectedDonorSchedules={[workingDonorSchedule]}
        renderFooter={renderConfirmationDialogFooter}
      />
    </>
  );
};
